'use client';

import { useEffect, useState, useId } from 'react';

import { isInChina } from '../utils/geo-ip';
import { getRecaptchaLocale, FSLocale } from '@fshr/shared';

import { getConfig } from '../config/config';

declare global {
	interface Window {
		grecaptcha: any;
		__recaptchaScriptLoaded?: boolean;
		__recaptchaOnloadCallbacks?: (() => void)[];
	}
}

interface useRecaptchaProps {
	fsLocale: FSLocale;
}

export default function useRecaptcha({ fsLocale }: useRecaptchaProps) {
	const config = getConfig();
	const {
		FSHR_PUBLIC_GOOGLE_RECAPTCHA_PUBLIC_KEY,
		GOOGLE_RECAPTCHA_PUBLIC_KEY,
		GOOGLE_RECAPTCHA_SCRIPT_PATH,
		CHINESE_RECAPTCHA_SCRIPT_PATH,
	} = config;

	const RECAPTCHA_PUBLIC_KEY =
		FSHR_PUBLIC_GOOGLE_RECAPTCHA_PUBLIC_KEY || GOOGLE_RECAPTCHA_PUBLIC_KEY;

	const GOOGLE_RECAPTCHA_PATH =
		GOOGLE_RECAPTCHA_SCRIPT_PATH || 'https://www.google.com/recaptcha/api.js';

	const CHINESE_RECAPTCHA_PATH =
		CHINESE_RECAPTCHA_SCRIPT_PATH || 'https://www.recaptcha.net/recaptcha/api.js';

	const uniqueId = useId();
	const sanitizedId = uniqueId.replace(/:/g, '');
	const recaptchaElementId = `recaptcha-${sanitizedId}`;
	const recaptchaCallbackName = `recaptchaSubmitConfirmation-${sanitizedId}`;

	const [isRecaptchaScriptLoaded, setIsRecaptchaScriptLoaded] = useState(false);
	const [widgetId, setWidgetId] = useState(null);

	useEffect(() => {
		if (isRecaptchaScriptLoaded) {
			const element = document.getElementById(recaptchaElementId);
			if (element && element.hasChildNodes()) {
				return; // Element already has reCAPTCHA rendered
			}

			setWidgetId(
				window.grecaptcha.render(recaptchaElementId, {
					sitekey: RECAPTCHA_PUBLIC_KEY,
					theme: 'light',
					size: 'invisible',
				})
			);
		}
	}, [isRecaptchaScriptLoaded]);

	useEffect(() => {
		// Check if already loaded globally
		if (window.__recaptchaScriptLoaded) {
			setIsRecaptchaScriptLoaded(true);
			return;
		}

		// Check if grecaptcha already exists (loaded by another instance)
		if (window.grecaptcha && window.grecaptcha.render) {
			setIsRecaptchaScriptLoaded(true);
			window.__recaptchaScriptLoaded = true;
			return;
		}

		// Initialize the callbacks array if it doesn't exist
		if (!window.__recaptchaOnloadCallbacks) {
			window.__recaptchaOnloadCallbacks = [];
		}

		// Add this instance's callback to the queue
		const myCallback = () => {
			setIsRecaptchaScriptLoaded(true);
		};
		window.__recaptchaOnloadCallbacks.push(myCallback);

		// Check if script already exists
		const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
		if (!existingScript) {
			// Create the shared global onload callback
			if (!(window as any).recaptchaGlobalOnload) {
				(window as any).recaptchaGlobalOnload = () => {
					window.__recaptchaScriptLoaded = true;
					// Call all waiting callbacks
					if (window.__recaptchaOnloadCallbacks) {
						window.__recaptchaOnloadCallbacks.forEach(cb => cb());
						window.__recaptchaOnloadCallbacks = [];
					}
				};
			}

			const recaptchaScript = isInChina()
				? `${CHINESE_RECAPTCHA_PATH}?render=explicit&onload=recaptchaGlobalOnload`
				: `${GOOGLE_RECAPTCHA_PATH}?hl=${getRecaptchaLocale({
						fsLocale,
					})}&render=explicit&onload=recaptchaGlobalOnload`;

			const script = document.createElement('script');
			script.setAttribute('async', '');
			script.setAttribute('defer', '');
			script.src = recaptchaScript;
			document.head.appendChild(script);
		}

		return () => {
			// Reset the widget if it exists
			if (widgetId !== null && window.grecaptcha && window.grecaptcha.reset) {
				try {
					window.grecaptcha.reset(widgetId);
				} catch (e) {
					// Widget might already be reset, ignore error
				}
			}

			// Remove this callback from the queue if script hasn't loaded yet
			if (window.__recaptchaOnloadCallbacks) {
				const index = window.__recaptchaOnloadCallbacks.indexOf(myCallback);
				if (index > -1) {
					window.__recaptchaOnloadCallbacks.splice(index, 1);
				}
			}
		};
	}, [fsLocale]);

	const recaptchaExecute = (): Promise<string> => {
		return new Promise(resolve => {
			(window as any)[recaptchaCallbackName] = (token: string) => {
				delete (window as any)[recaptchaCallbackName];
				resolve(token);
			};
			window.grecaptcha.execute(widgetId);
		});
	};

	const recaptchaReset = () => {
		if (isRecaptchaScriptLoaded && widgetId !== null) {
			window.grecaptcha.reset(widgetId);
		}
	};

	return {
		recaptchaElementId,
		recaptchaCallbackName,
		recaptchaKey: RECAPTCHA_PUBLIC_KEY,
		isReady: widgetId !== null && widgetId >= 0,
		recaptchaExecute: isRecaptchaScriptLoaded
			? recaptchaExecute
			: () => Promise.reject(new Error('reCAPTCHA is not loaded')),
		recaptchaReset,
	};
}
